'use client';

import { usePathname } from 'next/navigation';
import clsx from 'clsx';

import { Link } from '@northladder/i18n';

import { RenexisAppLogoSVG } from './brand';
// import { IMenuDropdownItem, MenuDropdown } from './dropdowns';
import { MenuIcon } from './icons';

import { ButtonAlt } from './ButtonAlt';
import { IMenuDropdownItem, MenuDropdown } from './dropdowns/MenuDropdown';

export interface IEnterprisePublicNavBar {
  /**
   * The href attribute for the navigation item's link.
   */
  href: string;
  /**
   * The text for the navigation item.
   */
  label: string;
}

export interface IUserData {
  /**
   * The initials of the user's name.
   */
  nameInitials: string;
  /**
   * Whether the user has a notification.
   */
  notification: boolean;
}

/**
 * Properties for the EnterprisePublicNavBar component.
 */
interface IEnterprisePublicNavBarProps {
  /** The navigation items to be displayed in the bar. */
  enterprisePublicNavBarItems: IEnterprisePublicNavBar[];
  /** Multilingual menu items for language selection. */
  multiLingualMenuItems: IMenuDropdownItem[];
  /** User data for displaying the user profile and notifications. */
  userData: IUserData;
}

/**
 * -----------------------------------------------------------------------------
 * This renders the navigation bar for the enterprise public pages.
 * TODO: @Sowed, Need to add dropdown for country and userProfile
 */
export function EnterprisePublicNavBar({
  enterprisePublicNavBarItems,
  multiLingualMenuItems,
  userData,
}: IEnterprisePublicNavBarProps) {
  const pathname = usePathname();
  // const router = useRouter();

  const { nameInitials, notification } = userData;

  // TODO: Remove this unused commented out code block
  // const redirectToQuoteRequest = () => {
  //   router.push('/enterprise/request-quote');
  // };

  return (
    <div
      className="
        fixed start-0 top-0 z-40 flex w-full items-center justify-between
        px-8 py-5 max-sm:px-4
      "
    >
      <div
        className="
          flex h-full items-center justify-between gap-16 rounded-full bg-gray-100
          px-8 py-3 max-lg:w-fit max-md:px-4 max-sm:px-3 max-sm:py-2
        "
      >
        <div className="flex items-center gap-5 max-md:gap-4 max-sm:gap-3">
          <span className="max-sm:h-8 max-sm:w-7 lg:hidden">
            <MenuIcon dark />
          </span>
          <Link className="max-sm:h-7 max-sm:w-20" href="/">
            <RenexisAppLogoSVG />
          </Link>
        </div>
        <div className="flex gap-8 text-lg max-lg:hidden">
          {enterprisePublicNavBarItems.map((item) => (
            <Link
              key={item.href}
              className={clsx(
                'cursor-pointer border-green-500 text-lg capitalize text-black',
                pathname === item.href && 'border-b'
              )}
              href={item.href}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-4 text-base">
        <ButtonAlt className="uppercase">Request Quote</ButtonAlt>
        <div className="max-lg:hidden">
          <MenuDropdown dark items={multiLingualMenuItems} />
        </div>
        <span className="h-2/4 border-e border-gray-400" />
        <ButtonAlt
          className={`
            relative aspect-square min-h-0 p-3.5 uppercase before:absolute
            before:bottom-0 before:end-0 before:h-3 before:w-3
            before:rounded-full before:content-[""] hover:bg-gray-950
            dark:bg-white ${notification && 'before:bg-green-500'}
            max-lg:hidden
          `}
          // dark
          // variant="primary"
        >
          {nameInitials}
        </ButtonAlt>
      </div>
    </div>
  );
}
